import "@/styles/overviewDashboard.css";
import { CrewNeedsShortcut } from "@/components/FieldCrewNeeds";
import { OfficeInstructions } from "@/components/ScheduleInstructions";
import FormRequirements from "@/components/FormRequirements";
import MySafetyHomeCard from "@/components/MySafetyActions";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ArrowUpRight, ArrowRight, Clock, Clock3, ShieldAlert, Hammer, Truck, Users, CalendarDays, MapPin, Navigation, FileSignature, Boxes, Phone, Trophy, ClipboardCheck, Zap, HardHat, BriefcaseBusiness, Fuel, ChevronDown, CloudSun, TriangleAlert, ListChecks, Wrench, Camera, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { JobPlans } from "@/components/JobPlans";
import TruckerHome from "@/components/TruckerHome";
import { XeroFinancialSnapshot } from "@/components/XeroFinancialSnapshot";
import { EnableNotificationsCard } from "@/components/EnableNotifications";
import { TodaysRunSheet } from "@/components/TodaysRunSheet";
import { SsspSignPrompt } from "@/components/SsspSignPrompt";
import FieldProgressCard from "@/components/FieldProgressCard";
import { GeofenceSignin } from "@/components/GeofenceSignin";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { GuidedOnboardingBanner } from "@/components/GuidedOnboardingBanner";
import LiveActivityFeed from "@/components/LiveActivityFeed";
import FleetComplianceAlerts from "@/components/FleetComplianceAlerts";
import { OverviewCharts, CompanyPulse } from "@/components/OverviewCharts";
import { MobileAppPromo } from "@/components/MobileAppPromo";
import { ClockInCard } from "@/components/ClockInCard";
import { CrewLiveMap } from "@/components/CrewLiveMap";
import { DashboardPanels, WelcomeBanner } from "@/components/DashboardPanels";
import PlantServiceDueWidget from "@/components/PlantServiceDueWidget";
import WeatherRiskWidget from "@/components/WeatherRiskWidget";
import SetupChecklist from "@/components/SetupChecklist";
import { DashboardSettings } from "@/components/DashboardSettings";
import { TourVideoButton } from "@/components/TourVideo";
import { FieldWeather } from "@/components/FieldWeather";

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [prefs, setPrefs] = useState({});
  const [extras, setExtras] = useState(null);
  const [teamOnline, setTeamOnline] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/org/ui-prefs").then((r) => setPrefs(r.data || {})).catch(() => {});
    if (user.role === "field") {
      api.get("/field/home-extras").then((r) => setExtras(r.data)).catch(() => {});
    } else {
      api.get("/field/drawer-stats").then((r) => setTeamOnline(r.data.staff_online)).catch(() => {});
    }
  }, [user.role]);

  const show = (k) => prefs[k] !== false;

  const isOffice = user.role !== "field";
  const isAdmin = ["admin", "super_admin"].includes(user.role);

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Mōrena" : h < 17 ? "Kia ora" : "Pō mārie";
  })();

  return (
    <div data-testid="dashboard-overview" className={isOffice ? "overview-office min-h-screen bg-[#F7F8FA] dark:bg-[#07090D]" : "field-hud"}>
      <div className={isOffice ? "px-5 py-5 lg:px-7 lg:py-6 w-full" : "field-home px-4 py-5 sm:px-6"}>
        {!isOffice && <><FieldHero user={user} /><CrewNeedsShortcut /></>}

        {isOffice ? (
          <>
            <WelcomeBanner name={user.name.split(" ")[0]}>
              <TourVideoButton />
              {(user.role === "admin" || user.role === "super_admin") && (
                <span className="[&>button]:bg-white [&>button]:text-zinc-900 [&>button]:border-white [&>button]:hover:bg-zinc-100 [&>button]:rounded-lg dark:[&>button]:bg-white/10 dark:[&>button]:text-white dark:[&>button]:border-white/25 dark:[&>button]:hover:bg-white/20">
                  <DashboardSettings prefs={prefs} onChanged={setPrefs} />
                </span>
              )}
            </WelcomeBanner>
            <GuidedOnboardingBanner />
            <OnboardingChecklist />
            {/* KPI cards — real /stats data with week-on-week movement */}
            {show("kpis") && <section className="mb-5" aria-label="Operational summary">
              <h2 className="text-base font-semibold mb-3">Operational summary</h2>
              <div className="overview-kpis">
                <KpiCell to="/app/jobs" label="Jobs in progress" value={stats.jobs_in_progress ?? "—"} testid="kpi-jobs" icon={BriefcaseBusiness} tone="orange" />
                <KpiCell to="/app/team" label="Team members" value={stats.team_total ?? "—"} testid="kpi-team" icon={Users} tone="blue" />
                <KpiCell to="/app/fleet" label="Active Fleet" value={stats.fleet_active ?? "—"} testid="kpi-fleet" icon={Truck} tone="green" />
                <KpiCell to="/app/timesheets" label="Timesheets awaiting approval" value={stats.pending_timesheets ?? "—"} testid="kpi-ts" icon={Clock} tone="purple" />
              </div>
            </section>}
            <div className={`grid gap-6 items-start ${show("live_jobs") && (show("alert_panels") || show("activity_feed")) ? "lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]" : "grid-cols-1"}`}>
              {show("live_jobs") && <LiveJobs />}
              <div className="min-w-0 space-y-4">
                {show("alert_panels") && <DashboardPanels stats={stats} rail />}
                {show("activity_feed") && <LiveActivityFeed />}
              </div>
            </div>
            <div className="mt-6 space-y-3">
              {isAdmin && <details className="border-b border-border py-3"><summary className="cursor-pointer text-base font-semibold">Business summary</summary><div className="mt-4"><BusinessPulse /></div></details>}
              {show("charts_hours_day") && <details className="border-b border-border py-3"><summary className="cursor-pointer text-base font-semibold">Company trends</summary><div className="mt-4"><CompanyPulse /></div></details>}
            </div>

            {show("alert_panels") && <DashboardPanels stats={stats} overdueOnly />}

            <div className="mt-6">
              <MobileAppPromo />
            </div>

            {/* Civil Link launcher hidden — Civil Link is moving to its own platform */}

            {/* Financials from Xero — admin only. Shows a CTA if Xero
                isn't connected yet so the section is still discoverable. */}
            {show("financials") && (user.role === "admin" || user.role === "super_admin") && (
              <div className="mt-8">
                <XeroFinancialSnapshot compact={true} />
              </div>
            )}

            {show("setup_checklist") && <div className="mt-8"><SetupChecklist /></div>}

            <details className="hard-card p-4 mt-6"><summary className="cursor-pointer text-base font-semibold">More operational trends</summary><OverviewCharts show={show} /></details>

            {show("fleet_alerts") && <div className="mt-8"><FleetComplianceAlerts /></div>}
            {show("crew_map") && <div className="mt-8"><CrewLiveMap /></div>}
            {show("plant_service") && <div className="mt-8"><PlantServiceDueWidget /></div>}
            {show("weather") && <div className="mt-8"><WeatherRiskWidget /></div>}

            <details className="border-t border-border mt-6 py-3"><summary className="cursor-pointer text-base font-semibold">More shortcuts</summary><QuickAccessRow /></details>
          </>
        ) : (
          <>
            <QuickActionsRow />
            <ClockInCard />
            <div className="mb-4"><FieldWeather variant="strip" /></div>
            <GeofenceSignin />
            <h2 className="field-section-heading">Your day</h2>
            <TodaysRunSheet />
            <div className="field-attention space-y-4 mb-5"><FormRequirements compact /><MySafetyHomeCard /></div>
            {user.truck_driver && <TruckerHome compact />}
            {(() => {
              // Field worker can have multiple jobs for tomorrow (split shift).
              // Fall back to the legacy single `my_next_job` if `my_next_jobs`
              // isn't present in the response.
              const jobs = stats.my_next_jobs && stats.my_next_jobs.length > 0
                ? stats.my_next_jobs
                : (stats.my_next_job ? [stats.my_next_job] : []);
              if (jobs.length === 0) {
                return (
                  <TomorrowJobCard
                    job={null}
                    noWork={stats.my_tomorrow_no_work}
                    forDate={stats.my_tomorrow_date}
                  />
                );
              }
              return jobs.map((j, i) => (
                <TomorrowJobCard
                  key={j.id || i}
                  job={j}
                  noWork={false}
                  forDate={stats.my_tomorrow_date}
                  index={i}
                  total={jobs.length}
                />
              ));
            })()}
            <TodaysActivityCard activity={extras?.activity} />
            <WeeklyHoursCard stats={stats} />
            <RecentJobsSection jobs={extras?.recent_jobs} />

            <details className="field-week mb-5">
              <summary>Your progress & team <ChevronDown className="w-4 h-4" /></summary>
              <div className="pt-4"><FieldQuickStats /><FieldProgressCard /></div>
            </details>
            <h2 className="field-section-heading">Tools & resources</h2>
            {/* Quick actions — big emblem tile grid */}
            <div className="grid grid-cols-2 gap-3" data-testid="home-action-list">
              <HudTile to="/app/safety" icon={ShieldAlert} title="Report hazard" sub="Photo + log it now" color="#FF3B30" testid="quick-safety" />
              {user.truck_driver && (
                <HudTile to="/app/running-sheets" icon={Truck} title="Running sheet" sub="Loads & tipping" color="#FF5F15" testid="quick-running-sheet" />
              )}
              {user.contractor && (
                <HudTile to="/app/contractor-invoices" icon={FileSignature} title="My invoices" sub="Bill your hours" color="#FF5F15" testid="quick-contractor-invoices" />
              )}
              <HudTile to="/app/fuel" icon={Fuel} title="Fuel fill-up" sub="Log diesel & petrol" color="#FFD60A" testid="quick-fuel" />
              <HudTile to="/app/qa" icon={ClipboardCheck} title="QA sign-off" sub="Tick finished work" color="#FF5F15" testid="quick-qa" />
              <HudTile to="/app/leaderboard" icon={Trophy} title="Leaderboard" sub="Where you sit" color="#39FF14" testid="nav-leaderboard" />
              <HudTile to="/app/variations" icon={FileSignature} title="Variations" sub="Extra work on site" color="#FF5F15" testid="quick-variations" />
              <HudTile to="/app/jobs" icon={Hammer} title="My jobs" sub="Today & tomorrow" color="#C0C4CC" testid="quick-jobs" />
              <HudTile to="/app/leave" icon={CalendarDays} title="Leave" sub="Book time off" color="#39FF14" testid="quick-leave" />
              <HudTile to="/app/suppliers" icon={Phone} title="Suppliers" sub="Trade phone book" color="#38BDF8" testid="nav-suppliers" />
              <HudTileCompact to="/app/materials" icon={Boxes} title="Yard / materials" sub="Order for site" color="#8B5CF6" testid="quick-yard" />
            </div>
            <div className="mt-5"><EnableNotificationsCard /></div>
          </>
        )}
      </div>
    </div>
  );
}

function QuickAction({ to, title, desc, icon: Icon, highlight }) {
  const fontHead = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };
  return (
    <Link
      to={to}
      className={`p-5 group transition-colors duration-150 ${
        highlight
          ? "bg-[#FF5F15]/10 text-zinc-900 dark:text-white hover:bg-[#FF5F15]/15"
          : "bg-white dark:bg-[#101620] hover:bg-zinc-50 dark:hover:bg-[#151C27]"
      }`}
    >
      <div className="flex items-center justify-between">
        <Icon className={`w-5 h-5 ${highlight ? "text-[#FF5F15]" : "text-zinc-500 dark:text-zinc-400 group-hover:text-[#FF5F15]"}`} strokeWidth={1.8} />
        <ArrowUpRight className={`w-4 h-4 ${highlight ? "text-[#FF5F15]" : "text-zinc-400 dark:text-zinc-600"} group-hover:text-[#FF5F15] transition-colors`} />
      </div>
      <h3
        className="text-[15px] uppercase tracking-[0.04em] mt-5 text-zinc-900 dark:text-white"
        style={{ ...fontHead, fontWeight: 800 }}
      >
        // {title}
      </h3>
      <p className={`text-[11px] uppercase tracking-[0.1em] mt-1 ${highlight ? "text-zinc-600 dark:text-zinc-300" : "text-zinc-500 dark:text-zinc-400"}`} style={{ ...fontHead, fontWeight: 700 }}>
        {desc}
      </p>
    </Link>
  );
}

function HudTile({ to, icon: Icon, title, sub, testid }) {
  return <Link to={to} data-testid={testid} className="field-tool">
    <span className="field-tool-icon"><Icon className="w-5 h-5" strokeWidth={1.8} /></span>
    <span className="block font-semibold text-sm mt-3">{title}</span>
    <span className="block text-xs text-zinc-500 dark:text-zinc-400 mt-1 leading-relaxed">{sub}</span>
  </Link>;
}
function HudTileCompact(props) { return <HudTile {...props} />; }

// ============================================================
//  COCKPIT HERO + KPI — Direction 2 "Construction Cockpit"
//  Live office home header. Uses Barlow Condensed (loaded globally
//  from index.css) and the slashy `//` motif.
// ============================================================
function CockpitHero({ greeting, userName }) {
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-NZ", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timeStr = now.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit", hour12: false });
  return (
    <div data-testid="cockpit-hero" className="mb-2">
      <div
        className="text-[11px] uppercase mb-2 text-zinc-500 dark:text-zinc-400"
        style={{ fontFamily: "'Barlow Condensed', system-ui, sans-serif", fontWeight: 700, letterSpacing: "0.22em" }}
      >
        {dateStr} · {timeStr} NZDT
      </div>
      <h1
        className="fl-page-title text-zinc-900 dark:text-white"
        style={{ fontFamily: "'Inter', system-ui, sans-serif", fontWeight: 700, letterSpacing: "0.01em" }}
      >

        {greeting}, {userName}
      </h1>
      <p className="mt-2 text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-400 max-w-2xl">
        Approvals, safety status and live job board at a glance.
      </p>
    </div>
  );
}

function BusinessPulse() {
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState(() => localStorage.getItem("pulse_period") || "month");
  const setPeriodPersist = (p) => { setPeriod(p); localStorage.setItem("pulse_period", p); };
  useEffect(() => {
    api.get("/finance/weekly_pulse", { params: { period } }).then((r) => setD(r.data)).catch(() => {});
  }, [period]);
  if (!d) return null;
  const isWeek = period === "week";
  const fmt = (n) => `$${(n || 0).toLocaleString("en-NZ", { maximumFractionDigits: 0 })}`;
  const pos = d.net >= 0;
  const costPct = d.income > 0 ? Math.min(100, Math.round((d.total_cost / d.income) * 100)) : 100;
  const tiles = [
    { label: "Overheads", sub: `${d.overheads_count} items /${isWeek ? "wk" : "mo"}`, val: d.overheads_weekly, c: "#8B5CF6" },
    { label: "Wages", sub: `${d.labour_hours}h worked`, val: d.labour_cost, c: "#F59E0B" },
    { label: "Machines", sub: `${d.machine_hours}h run`, val: d.machine_cost, c: "#3B82F6" },
    { label: "Total cost", sub: `To run the ${isWeek ? "week" : "month"}`, val: d.total_cost, c: "#FF5F15" },
    { label: "Income", sub: `${d.invoice_count} invoices`, val: d.income, c: "#22C55E" },
  ];
  return (
    <section className="ops-card p-4" data-testid="business-pulse">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Banknote className="w-4 h-4 text-[#FF5F15]" />
          <span className="font-bold text-[15px]">Business Pulse</span>
          <span className="text-[11px] font-semibold text-zinc-400 dark:text-zinc-500">· Last {d.days || (isWeek ? 7 : 30)} days</span>
          <div className="inline-flex rounded-lg border border-zinc-200 dark:border-white/10 overflow-hidden ml-1">
            {[["month", "Month"], ["week", "Week"]].map(([k, lbl]) => (
              <button key={k} type="button" onClick={() => setPeriodPersist(k)}
                className={`px-2.5 py-1 text-[11px] font-bold transition-colors ${period === k ? "bg-[#FF5F15] text-white" : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white"}`}
                data-testid={`pulse-period-${k}`}>
                {lbl}
              </button>
            ))}
            <span className="px-2.5 py-1 text-[11px] font-bold text-zinc-500 border-l border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/[0.04]">Admin only</span>
          </div>
        </div>
        <Link to="/app/financials" className="text-[11px] font-bold text-[#FF5F15] hover:underline" data-testid="pulse-view-details">
          View details →
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="min-w-0" data-testid={`pulse-${t.label.toLowerCase().replace(" ", "-")}`}>
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-zinc-500 dark:text-[#8B94A7]">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.c }} /> {t.label}
            </div>
            <div className="font-extrabold text-[22px] tabular-nums mt-1.5 tracking-tight">{fmt(t.val)}</div>
            <div className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-1">{t.sub}</div>
          </div>
        ))}
        <div className={`rounded-[10px] px-3 py-2 ${pos ? "bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-500/20" : "bg-rose-50 dark:bg-red-500/10 border border-rose-200/70 dark:border-red-500/20"}`} data-testid="pulse-net">
          <div className={`text-[11px] font-bold uppercase tracking-widest ${pos ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-red-400"}`}>
            {pos ? "Ahead by" : "Behind by"}
          </div>
          <div className={`font-extrabold text-[22px] tabular-nums mt-1.5 tracking-tight ${pos ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-red-400"}`}>
            {fmt(Math.abs(d.net))}
          </div>
          <div className="text-[11px] text-zinc-400 mt-1">income vs cost</div>
        </div>
      </div>
      <div className="mt-4">
        <div className="h-1.5 rounded-full bg-zinc-200 dark:bg-white/10 overflow-hidden">
          <div className={`h-full rounded-full ${costPct >= 100 ? "bg-red-500" : "bg-[#FF5F15]"}`} style={{ width: `${costPct}%` }} />
        </div>
        <div className="flex items-center justify-between gap-3 mt-2">
          <div className="text-[11px] text-zinc-400">
            {d.income > 0 ? `Costs are ${costPct}% of this ${isWeek ? "week" : "month"}'s invoiced income` : `No invoices issued in the last ${d.days || (isWeek ? 7 : 30)} days`}
          </div>
          <Link to="/app/branding" className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 hover:text-[#FF5F15] whitespace-nowrap transition-colors" data-testid="pulse-edit-overheads">
            Edit overheads →
          </Link>
        </div>
      </div>
    </section>
  );
}

function QuickAccessRow() {
  const items = [
    { to: "/app/jobs", label: "Jobs", desc: "View all jobs", icon: Hammer, c: "#FF5F15" },
    { to: "/app/timesheets", label: "Timesheets", desc: "Track & approve", icon: Clock, c: "#F59E0B" },
    { to: "/app/safety", label: "Safety", desc: "Stay compliant", icon: ShieldAlert, c: "#EF4444" },
    { to: "/app/fleet", label: "Fleet", desc: "Track vehicles", icon: Truck, c: "#8B5CF6" },
    { to: "/app/clients", label: "Clients", desc: "Contacts & sites", icon: Users, c: "#3B82F6" },
    { to: "/app/payroll", label: "Payroll", desc: "Run the pay week", icon: Banknote, c: "#22C55E" },
  ];
  return (
    <section className="mt-8 ops-card p-2.5" data-testid="quick-access-row">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        {items.map((q) => (
          <Link key={q.label} to={q.to}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-zinc-50 dark:hover:bg-white/[0.04] transition-colors"
                data-testid={`quick-access-${q.label.toLowerCase()}`}>
            <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${q.c}1C`, color: q.c }}>
              <q.icon className="w-[18px] h-[18px]" strokeWidth={2} />
            </span>
            <span className="min-w-0">
              <span className="block text-[13px] font-bold text-zinc-900 dark:text-white leading-tight">{q.label}</span>
              <span className="block text-[11px] text-zinc-500 dark:text-[#8B94A7] truncate">{q.desc}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

// Quick Actions — compact tile grid beside Business Pulse. Every tile is a real
// route the office/admin roles can already reach.
function QuickActionsCard() {
  const items = [
    { to: "/app/jobs", label: "New Job", icon: Hammer, c: "#FF5F15" },
    { to: "/app/timesheets", label: "Add Timesheet", icon: Clock, c: "#F59E0B" },
    { to: "/app/safety", label: "Log Hazard", icon: ShieldAlert, c: "#EF4444" },
    { to: "/app/running-sheets", label: "Create Docket", icon: ClipboardCheck, c: "#3B82F6" },
    { to: "/app/sssps", label: "Upload Docs", icon: Camera, c: "#8B5CF6" },
    { to: "/app/inspections", label: "Site Inspection", icon: ListChecks, c: "#0EA5E9" },
    { to: "/app/qa", label: "QA Sign-off", icon: ClipboardCheck, c: "#22C55E" },
    { to: "/app/leave", label: "Request Leave", icon: CalendarDays, c: "#6366F1" },
  ];
  return (
    <section className="ops-card p-4" data-testid="office-quick-actions">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#FF5F15]" />
          <span className="font-bold text-[15px]">Quick Actions</span>
        </div>
        <Link to="/app/profile" className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 hover:text-[#FF5F15] transition-colors" data-testid="quick-actions-edit">Edit</Link>
      </div>
      <div className="grid grid-cols-4 gap-2.5">
        {items.map((q) => (
          <Link key={q.label} to={q.to}
                className="group flex flex-col items-center gap-2 py-3 px-1 rounded-[10px] border border-zinc-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.02] hover:border-[#FF5F15]/40 hover:-translate-y-0.5 transition-[border-color,transform] duration-200"
                data-testid={`oqa-${q.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${q.c}1C`, color: q.c }}>
              <q.icon className="w-[17px] h-[17px]" strokeWidth={2} />
            </span>
            <span className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-200 text-center leading-tight">{q.label}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function KpiCell({ label, value, testid, to, icon: Icon, tone }) {
  return <Link to={to} className={`overview-kpi overview-tone-${tone}`} data-testid={testid}>
    <span className="overview-kpi-icon"><Icon className="w-6 h-6" aria-hidden="true" /></span>
    <div className="min-w-0"><div className="text-xs font-semibold text-slate-600 dark:text-slate-300 leading-snug">{label}</div>
    <div className="mt-2 text-3xl font-bold tracking-tight tabular-nums text-slate-950 dark:text-white">{value}</div></div>
  </Link>;
}
// iOS-style grouped list row with leading icon tile + chevron.

function WeeklyHoursCard({ stats }) {
  const total = stats.my_week_hours ?? 0;
  const pending = stats.my_week_hours_pending ?? 0;
  const approved = stats.my_week_hours_approved ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((approved / total) * 100)) : 0;
  const ws = stats.my_week_start;
  const we = stats.my_week_end;
  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-NZ", { day: "numeric", month: "short" }) : "";
  return (
    <div className="rounded-2xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] text-zinc-900 dark:text-white p-5 mt-3" data-testid="week-hours-hero">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 dark:text-zinc-400">This week</div>
        {ws && (
          <div className="text-[11px] font-mono text-zinc-400 uppercase tracking-widest">
            {fmt(ws)} – {fmt(we)}
          </div>
        )}
      </div>
      <div className="flex items-end gap-1.5 mt-2.5">
        <span className="font-mono font-bold text-5xl leading-none text-zinc-900 dark:text-white">{total.toFixed(1)}</span>
        <span className="text-[11px] font-bold uppercase text-zinc-400 dark:text-zinc-500 mb-0.5">hrs</span>
      </div>
      <div className="text-[11px] font-mono text-zinc-500 dark:text-zinc-400 mt-2.5">
        {approved.toFixed(1)} approved · {pending.toFixed(1)} pending
      </div>
      <div className="mt-3 h-1 rounded-full bg-zinc-200 dark:bg-white/10"><div className="h-full rounded-full bg-[#FF5F15]" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function TomorrowJobCard({ job, noWork, forDate, index, total }) {
  const dayLabel = forDate
    ? new Date(forDate).toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "short" })
    : "Tomorrow";
  if (noWork) {
    return (
      <div className="rounded-2xl bg-white dark:bg-[#101620] border border-zinc-200 dark:border-white/10 p-4 mb-4 flex items-center gap-4" data-testid="tomorrow-job-no-work">
        <span className="w-14 h-14 rounded-full border border-[#FF5F15]/40 bg-[#FF5F15]/10 flex items-center justify-center shrink-0">
          <CalendarDays className="w-6 h-6 text-[#FF5F15]" />
        </span>
        <div className="min-w-0">
          <div className="label-tag">Tomorrow · {dayLabel}</div>
          <h3 className="font-display font-bold uppercase text-lg leading-tight text-zinc-900 dark:text-white mt-0.5">Day off</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-0.5">No work scheduled by your supervisor. Enjoy the break.</p>
        </div>
      </div>
    );
  }
  if (!job) {
    return (
      <div className="relative rounded-2xl bg-white dark:bg-[#101620] border border-zinc-200 dark:border-white/10 p-4 mb-4 flex items-center gap-4 overflow-hidden" data-testid="tomorrow-job-empty">
        <span className="w-14 h-14 rounded-full border border-[#FF5F15]/40 bg-[#FF5F15]/10 flex items-center justify-center shrink-0">
          <CalendarDays className="w-6 h-6 text-[#FF5F15]" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="label-tag">Tomorrow · {dayLabel}</div>
          <div className="text-[15px] text-zinc-700 dark:text-zinc-200 mt-1 leading-snug">
            No job scheduled for you yet.<br />Check back later or chat your supervisor.
          </div>
        </div>
        <svg
          className="absolute right-2 bottom-1 w-28 h-14 text-zinc-300 dark:text-white/15 pointer-events-none"
          viewBox="0 0 120 50" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinejoin="round" strokeLinecap="round" aria-hidden="true"
        >
          <path d="M2 48 L34 14 L50 32 L64 18 L118 48" />
          <path d="M28 20 L34 14 L40 20" />
        </svg>
        <span className="absolute top-4 right-4 w-2.5 h-2.5 rounded-full bg-[#FF5F15]" />
      </div>
    );
  }
  const mapsUrl = job.site_address
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.site_address)}`
    : null;
  const tomorrow = job.for_date
    ? new Date(job.for_date).toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "short" })
    : "Tomorrow";
  const start = job.dispatch_start_time || job.start_time || "Time unspecified";
  const hasSplit = typeof total === "number" && total > 1;
  return (
    <div className="rounded-2xl bg-white dark:bg-[#101620] border border-zinc-200 dark:border-white/10 p-5 mb-4" data-testid={hasSplit ? `tomorrow-job-card-${index}` : "tomorrow-job-card"}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="label-tag !text-[#FF5F15] flex items-center gap-2 flex-wrap">
            <span>Tomorrow · {tomorrow}</span>
            {hasSplit && (
              <span
                className="inline-flex items-center px-2 py-0.5 bg-[#FF5F15] text-white text-[11px] font-bold uppercase tracking-widest rounded-sm leading-none"
                data-testid={`tomorrow-job-split-${index}`}
              >
                Job {index + 1} of {total}
              </span>
            )}
          </div>
          <h3 className="font-display font-bold uppercase text-xl mt-1 leading-tight">{job.title}</h3>
          {job.client_name && (
            <div className="text-xs text-zinc-500 font-mono mt-0.5">{job.client_name}</div>
          )}
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-zinc-100 dark:bg-[#151C27] border border-zinc-200 dark:border-white/10 text-zinc-900 dark:text-white">
          <Clock className="w-3.5 h-3.5 text-[#FF5F15]" />
          <span className="font-mono text-sm font-bold">{start}</span>
        </div>
      </div>

      {job.site_address && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <MapPin className="w-4 h-4 text-zinc-500 shrink-0" />
          <span className="text-zinc-700 truncate">{job.site_address}</span>
        </div>
      )}

      <OfficeInstructions crew={job.crew_instruction} personal={job.scope_of_works}/>

      {job.crew_mates?.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
              Crew with you ({job.crew_mates.length})
            </div>
            <Link
              to="/app/chat"
              className="text-[11px] font-mono uppercase tracking-widest text-[#FF5F15] hover:text-[#E65100]"
              data-testid="tomorrow-mate-noshow"
            >
              No-show? Ping office →
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {job.crew_mates.map((m) => (
              <TomorrowCrewMateChip key={m.id} mate={m} testid={`tomorrow-mate-${m.id}`} />
            ))}
          </div>
        </div>
      )}

      {job.description && (
        <div className="mt-3 border-l-2 border-[#FF5F15] bg-orange-50/50 p-3" data-testid="tomorrow-job-scope">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF5F15] mb-1.5">Scope of works</div>
          <p className="text-xs font-mono whitespace-pre-wrap leading-relaxed text-zinc-800">{job.description}</p>
        </div>
      )}

      {job.plans?.length > 0 && (
        <div className="mt-3" data-testid="tomorrow-job-plans">
          <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">
            Plans & drawings ({job.plans.length})
          </div>
          <JobPlans jobId={job.id} plans={job.plans} editable={false} compact={true} />
        </div>
      )}

      {/* SSSPs the worker still needs to sign before this job */}
      <SsspSignPrompt items={job.unsigned_sssps} testidPrefix={`tomorrow-${job.id}`} />

      <div className="mt-4 flex gap-2 flex-wrap">
        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 px-3 py-2 bg-[#FF5F15] hover:bg-[#E65100] text-white text-xs font-bold uppercase tracking-wider rounded-sm transition-colors"
             data-testid="tomorrow-job-directions">
            <Navigation className="w-3.5 h-3.5" /> Get directions
          </a>
        )}
        <Link to={`/app/jobs/${job.id}`}
              className="inline-flex items-center gap-2 px-3 py-2 border border-zinc-900 hover:bg-zinc-900 hover:text-white text-zinc-900 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors"
              data-testid="tomorrow-job-details">
          <Hammer className="w-3.5 h-3.5" /> Job details
        </Link>
      </div>
    </div>
  );
}



// Slightly larger version of the crew-mate chip used inside the Tomorrow tile.
// Shows initials + full first name + role + tap-to-call. Mirrors the run-sheet
// chip but a touch roomier since the Tomorrow card has more real estate.
function TomorrowCrewMateChip({ mate, testid }) {
  const first = (mate.name || "").split(" ")[0] || mate.name || "";
  const initials = (mate.name || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const phone = (mate.phone || "").trim();
  const telHref = phone ? `tel:${phone.replace(/[^\d+]/g, "")}` : null;
  return (
    <div
      className="inline-flex items-stretch border border-zinc-300 bg-white text-xs overflow-hidden shadow-[2px_2px_0_0_#09090B]"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 px-2.5 py-1">
        <span className="w-6 h-6 bg-zinc-900 text-white rounded-full flex items-center justify-center text-[11px] font-bold">
          {initials}
        </span>
        <div className="leading-tight">
          <div className="font-bold text-zinc-900">{first}</div>
          {mate.role && (
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
              {mate.role}
            </div>
          )}
        </div>
      </div>
      {telHref ? (
        <a
          href={telHref}
          className="px-2.5 flex items-center bg-[#FF5F15] text-white hover:bg-[#E65100] transition-colors"
          aria-label={`Call ${first}`}
          data-testid={`${testid}-call`}
        >
          <Phone className="w-3.5 h-3.5" />
        </a>
      ) : (
        <span
          className="px-2.5 flex items-center bg-zinc-100 text-zinc-400"
          title="No phone on file"
        >
          <Phone className="w-3.5 h-3.5" />
        </span>
      )}
    </div>
  );
}

// ============================================================
//  PREMIUM FIELD HOME — hero, quick stats, quick actions,
//  today activity + recent jobs (mockup-matched redesign)
// ============================================================
const WX_LABEL = (code) => {
  if (code == null) return null;
  if (code <= 1) return "Sunny";
  if (code <= 3) return "Cloudy";
  if (code <= 48) return "Fog";
  if (code <= 67) return "Rain";
  if (code <= 77) return "Snow";
  if (code <= 82) return "Showers";
  return "Storm";
};

function FieldHero({ user }) {
  const [site, setSite] = useState(null);
  const [siteState, setSiteState] = useState("loading");
  const [siteIsToday, setSiteIsToday] = useState(true);
  const [wx, setWx] = useState(null);
  useEffect(() => {
    let live = true;
    const loadSite = () => api.get("/runsheet/today").then(({ data }) => {
      if (!live) return;
      const next = data.site || data.jobs?.[0] || null;
      setSite(next);
      setSiteIsToday(!next?.scheduled_date || next.scheduled_date === data.for_date);
      setSiteState("ready");
    }).catch(() => { if (live) setSiteState("error"); });
    loadSite();
    window.addEventListener("focus", loadSite);
    if (navigator.geolocation && navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" }).then((p) => {
        if (p.state !== "granted") return;
        navigator.geolocation.getCurrentPosition((pos) => {
          api.get("/weather/forecast", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } })
            .then((r) => {
              const today = new Date().toISOString().slice(0, 10);
              const d = (r.data.days || []).find((x) => x.date === today) || (r.data.days || [])[0];
              if (d) setWx(d);
            }).catch(() => {});
        }, () => {}, { timeout: 8000, maximumAge: 600000 });
      }).catch(() => {});
    }
    return () => { live = false; window.removeEventListener("focus", loadSite); };
  }, []);
  const h = new Date().getHours();
  const greet = h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  const dateStr = new Date().toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" });
  const cond = WX_LABEL(wx?.weather_code);
  return <header className="field-home-header" data-testid="field-hud-header">
    <p className="field-eyebrow" data-testid="hero-date">{dateStr}</p>
    <h1>{greet}, {user.name.split(" ")[0]}<span className="text-[#FF5F15]">.</span></h1>
    <p className="field-home-subtitle">Your workday, all in one place.</p>
    {site?.id ? <Link to={`/app/jobs/${encodeURIComponent(site.id)}`} className="field-site-link" data-testid="hero-site-card">
      <span className="field-site-icon"><HardHat className="w-5 h-5" /></span>
      <span className="min-w-0 flex-1"><span className="block text-xs text-zinc-500 dark:text-zinc-400">{siteIsToday ? "Today's site" : "Last scheduled site"}</span><span className="block text-sm font-semibold truncate">{site.title || "Open scheduled job"}</span></span>
      <ArrowRight className="w-4 h-4 text-zinc-400" />
    </Link> : <div className="field-site-link text-sm text-zinc-500 dark:text-zinc-400" role="status" data-testid="hero-site-card">{siteState === "loading" ? "Loading your site…" : siteState === "error" ? "Unable to load your scheduled site." : "No site scheduled yet."}</div>}
    {wx && <p className="text-xs mt-3 text-zinc-500 dark:text-zinc-400" data-testid="hero-weather">{wx.temp_max != null ? `${Math.round(wx.temp_max)}° · ` : ""}{cond}</p>}
  </header>;
}

function FieldQuickStats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/field/drawer-stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  const cards = [
    { icon: BriefcaseBusiness, value: s?.active_jobs ?? "\u2014", label: "Active Jobs", trend: "In progress", to: "/app/jobs" },
    { icon: Users, value: s?.staff_online ?? "\u2014", label: "Staff Online", trend: "View team \u2192", to: "/app/chat" },
    { icon: TriangleAlert, value: s?.safety_alerts ?? "\u2014", label: "Safety Alerts", trend: "View alerts \u2192", to: "/app/safety", alert: (s?.safety_alerts || 0) > 0 },
  ];
  return (
    <div className="grid grid-cols-3 gap-3 mb-4" data-testid="field-quick-stats">
      {cards.map((c) => (
        <Link key={c.label} to={c.to}
              className="rounded-[20px] bg-white dark:bg-[#101620]/90 border border-zinc-200 dark:border-[#1F2937] backdrop-blur px-2 py-4 flex flex-col items-center gap-1.5 active:scale-[0.97] transition-transform"
              data-testid={`field-stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <c.icon className={`w-6 h-6 ${c.alert ? "text-red-500" : "text-[#FF5F15]"}`} strokeWidth={1.9} />
          <span className="font-display font-black text-[28px] leading-none text-zinc-900 dark:text-[#F8FAFC]">{c.value}</span>
          <span className="text-[11px] font-semibold text-zinc-600 dark:text-zinc-300 leading-none">{c.label}</span>
          <span className="text-[11px] text-[#FF5F15] font-medium leading-none">{c.trend}</span>
        </Link>
      ))}
    </div>
  );
}

function QuickActionsRow() {
  const actions = [
    { to: "/app/timesheets?new=1", label: "Log Hours", icon: Clock },
    { to: "/app/toolbox", label: "Toolbox", icon: ClipboardCheck },
    { to: "/app/fuel", label: "Fuel", icon: Fuel },
    { to: "/app/safety", label: "Hazard", icon: ShieldAlert },
    { to: "/app/prestarts", label: "Pre-start", icon: Wrench },
  ];
  return (
    <div className="mb-4" data-testid="field-quick-actions-row">
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 dark:text-[#94A3B8] mb-2.5 px-0.5">Quick Actions</div>
      <div className="field-shortcuts grid grid-cols-2 gap-2.5">
        {actions.map((a) => (
          <Link key={a.label} to={a.to}
                className={`field-shortcut ${a.label === "Log Hours" ? "field-shortcut-primary col-span-2" : ""}`}
                data-testid={`fqa-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <a.icon className="w-5 h-5 text-[#FF5F15]" strokeWidth={1.9} />
            <span className="text-sm font-semibold">{a.label === "Log Hours" ? "Log your hours" : a.label === "Hazard" ? "Report hazard" : a.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function TodaysActivityCard({ activity }) {
  const items = [
    { icon: Clock, value: activity ? `${activity.hours_logged}h` : "\u2014", label: "Hours" },
    { icon: ListChecks, value: activity?.tasks_completed ?? "\u2014", label: "Tasks Done" },
    { icon: ClipboardCheck, value: activity?.toolbox_talks ?? "\u2014", label: "Toolbox" },
    { icon: Fuel, value: activity?.fuel_entries ?? "\u2014", label: "Fuel" },
  ];
  return (
    <div className="rounded-[20px] bg-white dark:bg-[#101620]/90 border border-zinc-200 dark:border-[#1F2937] p-4 mt-1 mb-4" data-testid="todays-activity-card">
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 dark:text-[#94A3B8] mb-3">Today's Activity</div>
      <div className="grid grid-cols-4 divide-x divide-zinc-100 dark:divide-[#1F2937]">
        {items.map((i) => (
          <div key={i.label} className="flex flex-col items-center gap-1 px-1">
            <i.icon className="w-4 h-4 text-[#FF5F15]" strokeWidth={2} />
            <span className="font-display font-black text-lg leading-none text-zinc-900 dark:text-[#F8FAFC]">{i.value}</span>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-[#94A3B8] text-center leading-tight">{i.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const JOB_STATUS_META = {
  in_progress: { label: "Active", cls: "border-[#FF5F15]/50 text-[#FF5F15] bg-[#FF5F15]/10" },
  scheduled: { label: "Scheduled", cls: "border-sky-500/50 text-sky-500 bg-sky-500/10" },
  completed: { label: "Done", cls: "border-emerald-500/50 text-emerald-500 bg-emerald-500/10" },
  on_hold: { label: "On hold", cls: "border-amber-500/50 text-amber-500 bg-amber-500/10" },
};

function RecentJobsSection({ jobs }) {
  if (!jobs || jobs.length === 0) return null;
  return (
    <div className="mt-4 mb-1" data-testid="recent-jobs-section">
      <div className="flex items-center justify-between mb-2.5 px-0.5">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 dark:text-[#94A3B8]">Recent Jobs</div>
        <Link to="/app/jobs" className="text-[11px] font-bold text-[#FF5F15]">All jobs →</Link>
      </div>
      <div className="space-y-2.5">
        {jobs.map((j) => {
          const m = JOB_STATUS_META[j.status] || { label: j.status, cls: "border-zinc-500/40 text-zinc-400 bg-zinc-500/10" };
          return (
            <div key={j.id} className="rounded-[20px] bg-white dark:bg-[#101620]/90 border border-zinc-200 dark:border-[#1F2937] p-4" data-testid={`recent-job-${j.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-black tracking-tight text-[16px] leading-tight truncate text-zinc-900 dark:text-[#F8FAFC]">
                    {j.job_number ? `${j.job_number} \u2013 ` : ""}{j.title}
                  </div>
                  {j.site_address && <div className="text-[11px] text-zinc-500 dark:text-[#94A3B8] truncate mt-0.5">{j.site_address}</div>}
                </div>
                <span className={`shrink-0 border text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${m.cls}`}>{m.label}</span>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <div className="flex-1 h-1.5 rounded-full bg-zinc-100 dark:bg-[#1F2937] overflow-hidden">
                  <div className="h-full rounded-full bg-[#FF5F15]" style={{ width: `${j.progress ?? 0}%` }} />
                </div>
                <span className="text-[11px] font-mono text-zinc-500 dark:text-[#94A3B8] shrink-0">{j.progress != null ? `${j.progress}%` : "\u2014"}</span>
                <Link to={`/app/jobs/${j.id}`}
                      className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white bg-[#FF5F15] hover:bg-[#E65100] rounded-lg px-2.5 py-1.5 transition-colors"
                      data-testid={`recent-job-open-${j.id}`}>
                  Open <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
//  LIVE JOBS — office command centre module (tabs + job cards)
// ============================================================
const AUTH_TOKEN = () => localStorage.getItem("fl_token") || "";
const photoUrl = (path) =>
  `${api.defaults.baseURL}/files/view?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(AUTH_TOKEN())}`;

function LiveJobs() {
  const [jobs, setJobs] = useState(null);
  const [tab, setTab] = useState("in_progress");
  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => setJobs([]));
  }, []);
  const TABS = [["in_progress", "In Progress"], ["scheduled", "Upcoming"], ["completed", "Completed"]];
  const byTab = (s) => (jobs || []).filter((j) => j.status === s);
  const list = byTab(tab).slice(0, 5);
  const liveCount = byTab("in_progress").length;
  return (
    <section className="overview-jobs p-4 sm:p-5 flex flex-col min-w-0" data-testid="live-jobs-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-baseline gap-2.5 min-w-0">
          <span className="overview-panel-icon"><BriefcaseBusiness className="w-5 h-5" /></span>
          <span className="font-bold text-[15px] leading-none">Jobs</span>
          <span className="text-[11px] font-mono font-bold text-[#FF5F15] tracking-[0.2em] whitespace-nowrap">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-600 mr-1.5 align-middle" />
            {String(liveCount).padStart(2, "0")} ACTIVE
          </span>
        </div>
        <Link to="/app/jobs" className="text-[11px] font-bold text-[#FF5F15] hover:underline whitespace-nowrap" data-testid="live-jobs-view-all">View all →</Link>
      </div>

      <div className="inline-flex flex-wrap gap-1 self-start bg-zinc-100 dark:bg-white/[0.05] rounded-lg p-1 mt-4 mb-4">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" aria-pressed={tab === k} onClick={() => setTab(k)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                    tab === k ? "bg-[#FF5F15] text-white" : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
                  }`}
                  data-testid={`live-jobs-tab-${k}`}>
            {label}
            <span className={`text-[11px] font-mono px-1 py-px rounded leading-none ${tab === k ? "bg-black/25" : "bg-zinc-200 dark:bg-white/10"}`}>
              {byTab(k).length}
            </span>
          </button>
        ))}
      </div>

      {jobs === null ? (
        <div className="h-24 rounded-xl bg-zinc-100 dark:bg-white/5 animate-pulse" />
      ) : list.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center">No jobs in this state.</div>
      ) : (
        <div className="grid grid-cols-1 divide-y divide-slate-100 dark:divide-white/5">
          {list.map((j) => {
            const pct = j.task_total ? Math.round((100 * (j.task_done || 0)) / j.task_total) : null;
            const crew = (j.assigned_user_ids || []).length;
            const m = JOB_STATUS_META[j.status] || { label: j.status, cls: "border-zinc-500/40 text-zinc-400 bg-zinc-500/10" };
            return (
              <Link key={j.id} to={`/app/jobs/${j.id}`}
                    className="group block rounded-lg px-2 -mx-2 py-4 hover:bg-slate-50 dark:hover:bg-white/[0.03] focus-visible:ring-2 focus-visible:ring-[#FF5F15] transition-colors"
                    data-testid={`live-job-${j.id}`}>
                <div className="flex items-start gap-4">
                  {j.thumb_photo_path ? (
                    <img
                      src={photoUrl(j.thumb_photo_path)}
                      alt=""
                      loading="lazy"
                      className="w-[76px] h-[62px] shrink-0 rounded-lg object-cover border border-black/10 dark:border-white/10 hidden sm:block"
                      data-testid={`live-job-photo-${j.id}`}
                    />
                  ) : (
                    <span
                      className="w-[76px] h-[62px] shrink-0 rounded-lg border border-zinc-200 dark:border-white/[0.07] bg-slate-50 dark:bg-slate-800/40 hidden sm:flex flex-col items-center justify-center gap-1"
                      data-testid={`live-job-nophoto-${j.id}`}
                    >
                      <BriefcaseBusiness className="w-5 h-5 text-slate-400 dark:text-slate-500" strokeWidth={1.6} />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-bold text-[15px] leading-tight truncate text-slate-900 dark:text-slate-100">
                          {j.job_number ? <span className="text-[#FF5F15]">{j.job_number}</span> : null}
                          {j.job_number ? " — " : ""}{j.title}
                        </div>
                        {j.site_address && (
                          <div className="flex items-center gap-1 text-[11px] text-zinc-500 dark:text-[#8B94A7] mt-1 min-w-0">
                            <MapPin className="w-3 h-3 text-[#FF5F15] shrink-0" />
                            <span className="truncate">{j.site_address}</span>
                          </div>
                        )}
                      </div>
                      <span className={`shrink-0 border text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${m.cls}`}>{m.label}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] font-semibold text-zinc-500 dark:text-[#8B94A7]">
                      <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {crew} crew</span>
                      {j.task_total > 0 && (
                        <span className="inline-flex items-center gap-1.5"><ClipboardCheck className="w-3.5 h-3.5" /> {j.task_done || 0}/{j.task_total} tasks</span>
                      )}
                      {pct != null && (
                        <span className="ml-auto font-bold text-[12px] text-zinc-900 dark:text-white tabular-nums">{pct}% <span className="text-[11px] font-semibold text-zinc-400">complete</span></span>
                      )}
                    </div>
                    {pct != null && <div role="progressbar" aria-label="Job tasks completed" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} className="mt-2 h-1.5 rounded-full bg-zinc-200 dark:bg-white/10 overflow-hidden">
                      <div className={`h-full rounded-full transition-[width] duration-500 ${j.status === "on_hold" ? "bg-amber-500" : pct < 25 ? "bg-blue-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                    </div>}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
